import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';

import { ScreensStateService } from './screens-state.service';
import { UIService } from '../utils/ui.service';
import { BehaviorScreen } from '../../models/view-models/monitor-models/screen.type';
import { PlaylistsService } from '../http-services/playlists.service';
import { MonitorHub } from '../../../shared-module/hubs/monitor.hub';
import { PlaylistDTO } from '../../models/view-models/playlist-models/playlist-dto.model';
import { PlaylistItemDTO } from '../../models/view-models/playlist-item-models/playlist-item-dto.model';
import { PlaylistItemType } from '../../models/enums/playlist-item-type.enum';
import { PlaylistItemLinkModel } from '../../models/view-models/playlist-item-models/playlist-item-link.model';
import { AppConstants } from '../../../app.constants';
import { TransitionEffect } from '../../constants/transition-effect.const';
import { ShareContentInfo } from '../../interfaces/share-content-info.interface';

@Injectable()
export class PlaylistStateService implements OnDestroy {
  public playlistItemsSelectionChanged = new Subject<string[]>();

  public playlists: PlaylistDTO[] = [];
  subscriptions: Subscription = new Subscription();

  constructor(
    public playlistsDataService: PlaylistsService,
    public monitorHub: MonitorHub,
    public screenStateService: ScreensStateService,
    public toastrService: ToastrService,
    public uiService: UIService
  ) {

    this.subscriptions.add(this.getPlaylists());

    this.monitorHub.PlaylistsUpdated.pipe(
      takeUntilDestroyed(),
      tap((data) => {
        this.playlists = data;
        this.reloadPlaylists();
      })
    ).subscribe();

    this.monitorHub.PlaylistUpdated.pipe(
      takeUntilDestroyed()
    ).subscribe(data => {
      const playlistToUpdate = this.playlists.find(playlist => playlist.Id === data.Id);
      playlistToUpdate
        ? Object.assign(playlistToUpdate, data)
        : this.playlists.push(data);

      this.reloadPlaylists();
    });

    this.monitorHub.PlaylistRemoved.pipe(takeUntilDestroyed())
      .subscribe(id => {
        this.playlists = this.playlists.filter(playlist => playlist.Id !== id);
        this.reloadPlaylists();
      });

    this.subscriptions.add(this.screenStateService.someScreenUpdated.subscribe(
      data => {
        const ids = _.uniqBy(_.map(this.screenStateService._selectedScreens, (o) => o.IdAssignedPlaylist));
        this.screenStateService.playlistSelectionChanged.next(ids.length === 1 ?
          this.playlists.find(x => x.Id === ids[0]) : null);
      }
    ));

    this.subscriptions.add(this.screenStateService.selectedScreensChanged.subscribe(
      () => this.updatePlaylistSelection()
    ));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public static filterPlaylistsByScreens(playlists: PlaylistDTO[], screens: BehaviorScreen[]): PlaylistDTO[] {
    const screensMacs = _.uniqBy(_.map(screens, (screen: BehaviorScreen) => screen.MachinePhysicalAddress));
    if (screensMacs.length === 1) {
      return _.filter(
        playlists, (p: PlaylistDTO) =>
          (p.MachinePhysicalAddress === null || p.MachinePhysicalAddress === screensMacs[0]) &&
          p.IsAndroid === screens[0].Machine.IsAndroid
      );
    } else if (screensMacs.length > 1) {
      const isEveryScreensAndroid = screens.every(screen => screen.Machine.IsAndroid);
      const isEveryScreenWindows = screens.every(screen => !screen.Machine.IsAndroid);

      if (!isEveryScreensAndroid && !isEveryScreenWindows) {
        return [];
      }

      return _.filter(
        playlists, (p: PlaylistDTO) =>
          p.MachinePhysicalAddress === null &&
          (isEveryScreensAndroid ? p.IsAndroid: !p.IsAndroid)
      );
    }

    return playlists;
  }

  public getPlaylists() {
    return this.playlistsDataService.getAllPlaylists().subscribe(
      data => {
        this.playlists = data;
        this.reloadPlaylists();
        this.updatePlaylistSelection();
      }
    );
  }

  public createUrlPlaylist(shareContent: ShareContentInfo, isAndroid: boolean): PlaylistDTO {
    const playlist = new PlaylistDTO();
    playlist.PlaylistTitle = `${ AppConstants.SHARE_CONTENT_PLAYLIST_TITLE } [ ${ shareContent.title } ]`;
    playlist.IsAndroid = isAndroid;

    const playlistItem = new PlaylistItemDTO();
    playlistItem.PlaylistItemTitle = shareContent.link;
    playlistItem.PlaylistItemType = PlaylistItemType.Url;
    playlistItem.PlaylistItemLinks.push(new PlaylistItemLinkModel(shareContent.link));

    if (isAndroid) {
      playlist.Transition = TransitionEffect.Cut;
    } else {
      playlistItem.IsMuted = true;
      playlistItem.IsContentStretched = true;
      playlistItem.LauncherId = AppConstants.CHROME_LAUNCHER_ID;
    }

    playlist.PlaylistItems = [playlistItem];
    return playlist;
  }

  public changePlaylist(playlist: PlaylistDTO, isToStartPlaylist?: boolean) {
    this.synchPlaylists(playlist, isToStartPlaylist);
  }

  public synchPlaylists(playlist: PlaylistDTO, isToStartPlaylist: boolean) {
    if (playlist) {
      if (this.screenStateService._selectedScreens && this.screenStateService._selectedScreens.length) {
        this.screenStateService.updateAssignments(playlist, isToStartPlaylist);
      } else {
        this.uiService.showBottomPanel();
        this.toastrService.info('You should select at least one screen to assign a playlist', 'Playlist assignment info');
      }
    } else {
      this.screenStateService.playlistSelectionChanged.next(null);
    }
  }

  public reloadPlaylists() {
    this.playlistsDataService.playlistCollectionChanged.next(this.playlists);

    if (this.screenStateService.playlistSelectionChanged.value) {
      const selectedPlaylists = this.playlists.find(x => x.Id === (this.screenStateService.playlistSelectionChanged.value.Id));
      this.screenStateService.playlistSelectionChanged.next(selectedPlaylists);
    }
  }

  updatePlaylists(playlist: PlaylistDTO): void {
    const idx = this.playlists.findIndex(item => item.Id === playlist.Id);

    if (idx !== -1) {
      this.playlists.splice(idx, 1, playlist);
      this.playlistsDataService.playlistCollectionChanged.next(this.playlists);
    }
  }

  selectItemByIndex(indexes: string[]) {
    this.playlistItemsSelectionChanged.next(indexes);
  }

  private updatePlaylistSelection() {
    // find ids of assigned playlist for selection group
    const assignedPlaylistIds = [...new Set(this.screenStateService._selectedScreens.map(screen => screen.IdAssignedPlaylist))];
    // setup selected playlist if they all have the same playlist
    const selectedPlaylist = assignedPlaylistIds.length === 1 ? this.playlists.find(x => x.Id === assignedPlaylistIds[0]) : null;
    this.screenStateService.playlistSelectionChanged.next(selectedPlaylist);
  }
}
